import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from "recharts";
import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DailyData } from "@/lib/hyperliquid";

interface VolumeChartProps {
  data?: DailyData[];
  loading?: boolean;
  className?: string;
}

export const VolumeChart = ({ 
  data = [], 
  loading = false,
  className 
}: VolumeChartProps) => {
  if (loading) {
    return (
      <div className={cn("stat-card h-[400px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div className="h-5 w-32 bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-20 bg-muted/50 rounded animate-pulse" />
        </div>
        <div className="h-72 w-full bg-muted/30 rounded animate-pulse" />
      </div>
    );
  }

  const chartData = data.map(d => ({
    date: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    volume: d.volume,
    trades: d.trades,
    fees: d.fees
  }));

  const totalVolume = chartData.reduce((sum, d) => sum + d.volume, 0);
  const avgVolume = chartData.length > 0 ? totalVolume / chartData.length : 0;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload;
      return (
        <div className="bg-popover/95 backdrop-blur-sm border border-border/50 rounded-lg p-3 shadow-xl">
          <p className="text-xs text-muted-foreground mb-1.5">{label}</p>
          <p className="text-xl font-bold font-mono text-accent">
            ${payload[0].value.toLocaleString()}
          </p>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <span>{point.trades} trades</span>
            <span>•</span>
            <span>${Number(point.fees).toFixed(2)} fees</span>
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className={cn("stat-card h-[400px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base font-semibold mb-1">Daily Volume</h3>
            <span className="text-xs text-muted-foreground">Trading activity</span>
          </div>
        </div>
        <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
          No volume data available
        </div>
      </div>
    );
  }

  return (
    <div className={cn("stat-card h-[400px] group", className)}>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold">Daily Volume</h3>
              <div className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-accent/10 text-accent">
                <TrendingUp className="w-3 h-3" />
                {chartData.length} days
              </div>
            </div>
            <span className="text-xs text-muted-foreground">Trading activity</span>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold font-mono">
              ${avgVolume.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <div className="text-xs text-muted-foreground">Avg Daily</div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--border))" 
                strokeOpacity={0.3}
                vertical={false}
              />
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                dy={10}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.2 }} />
              <Bar 
                dataKey="volume" 
                fill="url(#barGradient)"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
